import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { defaultPathForRole } from "@/components/ProtectedRoute";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, rtl } = useLang();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(phone.trim(), password);
      toast.success(`${t("login.submit")} ✓`);
      nav(defaultPathForRole(u.role), { replace: true });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || t("login.error");
      setErr(typeof msg === "string" ? msg : t("login.error"));
      toast.error(t("login.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen relative bg-charcoal-900 text-foreground flex items-center justify-center px-5 grain-overlay">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(212,175,55,0.15),transparent_45%),radial-gradient(circle_at_80%_80%,rgba(109,190,69,0.1),transparent_40%)]" />
      <div className="absolute top-5 left-5">
        <Link to="/" data-testid="back-to-landing" className="text-sm text-foreground/60 hover:text-gold inline-flex items-center gap-2">
          <ArrowLeft className={`h-4 w-4 ${rtl ? "rotate-180" : ""}`} /> Java SPA
        </Link>
      </div>
      <div className="absolute top-5 right-5"><LanguageSwitcher /></div>

      <div className="relative w-full max-w-md card-luxury p-8 md:p-10">
        <div className="flex justify-center mb-8"><BrandLogo size="lg" showText={false} /></div>
        <h1 className="text-2xl md:text-3xl font-heading text-center">{t("login.title")}</h1>
        <p className="text-center text-sm text-foreground/50 mt-2 mb-8">
          {rtl ? "أدخل بياناتك" : "Enter your credentials"}
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("login.phone")}</label>
            <input
              data-testid="login-phone-input"
              type="text"
              autoComplete="username"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder=""
              className="w-full bg-charcoal-900 border border-charcoal-600 focus:border-gold focus:ring-2 focus:ring-gold/40 rounded-lg px-4 py-3 outline-none transition"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("login.password")}</label>
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-charcoal-900 border border-charcoal-600 focus:border-gold focus:ring-2 focus:ring-gold/40 rounded-lg px-4 py-3 outline-none transition"
              required
            />
          </div>

          {err && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full btn-gold justify-center disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
